import React, { createContext, useContext, useEffect, useReducer, useRef, useMemo, useState, useCallback } from 'react';
import { PageContext } from '../../contexts/AppContexts';
import { AppContext } from '../../global/AppContext';
import { submitFight, TAKEOUT_TYPES } from '../../global/request/bet';
import { areLocationsInRange, DELIVERY_RANGE, getAddressLocation } from '../../global/util';
import useGoogleMapsLoader from '../../googleMapsApi/useGoogleMapsLoader';
import { AuthContext } from '../authentication/AuthContext';
// TODO: can separate dispatch and state into separate contexts. can potentially avoid rerenders in components that don't depend on the state but need the dispatch.
const contextTemplate = {
  game: {},
  outcome: {},
  allOutcomeData: [],
  challenger: [],
  pageIndex: 0,
  canGo: [],
  items: [],
  payment: '',
  isPaymentRequired: false,
  changedDeliveryAddress: null, // only set if changed within modal, else use appctx.restaurant.address
  deliveryLatitude: 0,
  deliveryLongitude: 0,
  isChangedAddressAutocompleted: false,
  submitted: false,
  errorMessage: null,
  deliveryInstructions: '',
  isMatchedClicked: false,
  isReferClicked: false,
  foodfightId: null,
  isReferralLinkCopied: false,
  isReferFriendModalOpen: false,
  onShareLinkClicked: () => {},
};

export const ACTIONS = {
  SET_INITIAL_STATE: 'setInitialState',
  SET_GAME: 'setGame',
  SET_OUTCOME: 'setOutcome',
  SET_ALL_OUTCOME_DATA: 'setAllOutcomeData',
  CLEAR_CHALLENGER: 'clearChallenger',
  SET_CHALLENGER: 'setChallenger',
  PUSH_PAGE: 'pushPage',
  SET_PAGE_INDEX: 'setPageIndex',
  SET_CAN_GO: 'setCanGo',
  SET_ITEM: 'setItem',
  SET_PAYMENT: 'setPayment',
  SET_IS_PAYMENT_REQUIRED: 'setIsPaymentRequired',
  SET_ADDRESS: 'setAddress',
  SET_DELIVERY_LATITUDE: 'setDeliveryLatitude',
  SET_DELIVERY_LONGITUDE: 'setDeliveryLongitude',
  SET_ADDRESS_AUTOCOMPLETED: 'setAddressAutocompleted',
  SUBMITTED: 'submitted',
  DEV_SET_STATE: 'devSetState',
  SET_MATCHED_CLICKED: 'setMatchedClicked',
  SET_REFER_CLICKED: 'setReferClicked',
  SET_REFERRAL_LINK_COPIED: 'setReferralLinkCopied',
  SET_REFERRAL_MODAL_OPEN: 'setReferralModalOpen',
  FAIL_SUBMIT: 'failSubmit',
  SET_ERROR_MESSAGE: 'setErrorMessage',
  SET_DELIVERY_INSTRUCTIONS: 'setDeliveryInstructions',
  SET_FOODFIGHT_ID: 'setFoodFightId',
  SET_ON_SHARE_LINK_CLICKED: 'setOnShareLinkClicked',
};

/**
 * Context for the start fight flow. Contains the state of the flow and the
 * dispatch function to update the state.
 */

const StartFightContext = createContext({
  ...contextTemplate,
  dispatch: () => {},
});

/**
 * Context provider for the start fight flow. Contains the state of the flow and the
 * dispatch function to update the state. Also contains refs to the modal and nav components.
 * @param {Object} props
 * @param {React.MutableRefObject} props.modal A ref to the modal component.
 * @param {React.MutableRefObject} props.nav A ref to the nav component.
 * @param {React.MutableRefObject} props.reducerRef A ref to the reducer function. The provider mutates this ref.
 * @param {number} props.pageCount The number of pages in the flow.
 * @returns
 */
function StartFightContextProvider({ nav, reducerRef, pageCount, children }) {
  const navRef = useRef();
  const formRef = useRef(null);
  // TODO: remove later
  const authCtx = useContext(AuthContext);
  const appCtx = useContext(AppContext);
  const pageCtx = useContext(PageContext);
  const footerNav = useRef();
  const matchModalRef = useRef();
  const referModalRef = useRef();

  const { isLoaded } = useGoogleMapsLoader();
  const geocoderRef = useRef();

  const [fight, dispatch] = useReducer(
    (state, action) => {
      let updatedState = { ...state };
      switch (action.action) {
        case ACTIONS.SET_INITIAL_STATE: {
          updatedState = { ...updatedState, ...action.state };
          break;
        }

        case ACTIONS.SET_GAME: {
          updatedState.game = action.payload;
          break;
        }

        case ACTIONS.SET_OUTCOME: {
          updatedState.outcome = action.payload;
          break;
        }

        case ACTIONS.SET_ALL_OUTCOME_DATA: {
          updatedState.allOutcomeData = action.payload;
          break;
        }

        case ACTIONS.CLEAR_CHALLENGER: {
          updatedState.challenger = undefined;
          break;
        }

        case ACTIONS.SET_CHALLENGER: {
          updatedState.challenger = action.challenger;
          break;
        }

        case ACTIONS.SET_PAGE_INDEX: {
          updatedState.pageIndex = action.pageIndex;
          break;
        }
        case ACTIONS.SET_PAYMENT: {
          updatedState.payment = action.payment;
          break;
        }
        case ACTIONS.SET_IS_PAYMENT_REQUIRED: {
          updatedState.isPaymentRequired = action.payload;
          break;
        }

        case ACTIONS.SET_CAN_GO: {
          updatedState.canGo = [...state.canGo];
          updatedState.canGo[action.index] = action.canGo;
          break;
        }

        case ACTIONS.SET_MATCHED_CLICKED: {
          return { ...state, isMatchedClicked: action.payload };
        }

        case ACTIONS.SET_REFER_CLICKED: {
          return { ...state, isReferClicked: action.payload };
        }

        case ACTIONS.SET_ITEM: {
          updatedState.items = [...state.items];

          // TODO: can be consolidated by using the array find function
          // map the items id to index within items array
          const indexMap = Object.fromEntries(updatedState.items.map((item, index) => [item.id, index]));

          if (indexMap[action.item.id] !== undefined) {
            if (action.item.amount === 0) {
              updatedState.items.splice(indexMap[action.item.id], 1);
            } else {
              updatedState.items[indexMap[action.item.id]].amount = action.item.amount;
            }
          } else if (action.item.amount !== 0) {
            updatedState.items.push(action.item);
          }

          break;
        }

        case ACTIONS.SET_ADDRESS: {
          updatedState.changedDeliveryAddress = action.payload;
          break;
        }

        case ACTIONS.SET_ADDRESS_AUTOCOMPLETED: {
          updatedState.isChangedAddressAutocompleted = action.payload;
          break;
        }

        case ACTIONS.SET_ERROR_MESSAGE: {
          updatedState.errorMessage = action.payload;
          break;
        }

        case ACTIONS.SET_DELIVERY_INSTRUCTIONS: {
          updatedState.deliveryInstructions = action.payload;
          break;
        }

        case ACTIONS.SET_DELIVERY_LATITUDE: {
          updatedState.deliveryLatitude = action.payload;
          break;
        }

        case ACTIONS.SET_DELIVERY_LONGITUDE: {
          updatedState.deliveryLongitude = action.payload;
          break;
        }
        case ACTIONS.SUBMITTED: {
          return {
            ...updatedState,
            submitted: true,
          };
        }
        case ACTIONS.FAIL_SUBMIT: {
          updatedState.submitted = false;
          break;
        }

        case ACTIONS.DEV_SET_STATE: {
          updatedState = action.state;
          break;
        }

        case ACTIONS.SET_REFERRAL_MODAL_OPEN:
          return {
            ...state,
            isReferFriendModalOpen: action.payload,
          };

        case ACTIONS.SET_REFERRAL_LINK_COPIED:
          return {
            ...state,
            isReferralLinkCopied: action.payload,
          };

        case ACTIONS.SET_FOODFIGHT_ID:
          return {
            ...state,
            foodfightId: action.payload,
          };

        case ACTIONS.SET_ON_SHARE_LINK_CLICKED:
          return {
            ...state,
            onShareLinkClicked: action.payload,
          };

        default: {
          throw new Error('Invalid action');
        }
      }

      return updatedState;
    },
    { ...contextTemplate },
  );

  // const itemsTotal = useMemo(
  //   () => fight.items.map(item => item.amount * item.price).reduce((acc, cur) => acc + cur, 0),
  //   [fight?.items],
  // );

  const submitStripeForm = useCallback(async () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  }, [formRef]);

  const [isPaymentComplete, setPaymentComplete] = useState(false);

  useEffect(() => {
    if (nav) {
      navRef.current = nav.current;
    }

    if (reducerRef) {
      reducerRef.current = dispatch;
    }
  }, [nav, reducerRef]);

  useEffect(() => {
    if (isLoaded) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [isLoaded]);

  const handleSubmit = useCallback(async () => {
    const totalPrice = fight.items.map(item => item.price * item.amount).reduce((sum, cost) => sum + cost);
    if (totalPrice > 0 && fight.payment === '') {
      if (fight.game && fight.outcome && fight.challenger && fight.items.length > 0) {
        await submitStripeForm();
      }
    } else {
      const deliveryInfo = {
        address: fight.changedDeliveryAddress || appCtx.deliveryAddress,
        lat: fight.changedDeliveryAddress ? fight.deliveryLatitude : appCtx.deliveryLatitude,
        lng: fight.changedDeliveryAddress ? fight.deliveryLongitude : appCtx.deliveryLongitude,
        isAutocompleted: fight.changedDeliveryAddress
          ? fight.isChangedAddressAutocompleted
          : appCtx.isAddressAutocompleted,
        isLatLngReliable: true,
      };
      if (appCtx.isDelivery && !deliveryInfo.isAutocompleted) {
        const deliveryLocation = await getAddressLocation(geocoderRef.current, deliveryInfo.address);
        if (deliveryLocation === null) {
          console.warn('Could not get geolocation of delivery address:', deliveryInfo.address);
          deliveryInfo.isLatLngReliable = false;
        } else {
          deliveryInfo.lat = deliveryLocation.lat();
          deliveryInfo.lng = deliveryLocation.lng();
        }
      }
      // menu_item_id and product_id are separate to handle the difference between our storage
      // of menu_item ids as Integers. This is a temporary measure
      // as to not require making new tables and schemas to handle strings and keep other
      // restaurants operational
      const fightData = {
        restaurantId: appCtx.restaurant.id,
        items: fight.items.map(item => ({
          menu_item_id: Number.isInteger(item.id) ? item.id : -1,
          quantity: item.amount,
          price: item.price,
          name: item.name,
          product_id: Number.isInteger(item.id) ? '' : item.id,
        })),
        makerOutcome: fight.outcome.id,
        takerId: fight.challenger.id,
        payment: fight.payment,
        address: fight.changedDeliveryAddress || appCtx.deliveryAddress || '', // adding default value for people without addresses that are ordering in-person
        sport: fight.game.sport,
        event: fight.game.event,
        takeoutType: appCtx.isDelivery ? TAKEOUT_TYPES.DELIVERY : TAKEOUT_TYPES.IN_PERSON,
        deliveryInstructions: fight.deliveryInstructions,
        deliveryLat: deliveryInfo.lat,
        deliveryLng: deliveryInfo.lng,
        is_for_prize_offer: !appCtx.isDelivery,
      };

      if (appCtx.isDelivery) {
        if (['Jets Pizza', "Jet's Pizza"].includes(appCtx.restaurant.name) && totalPrice < 1000) {
          console.error("Jet's Pizza delivery is not available for orders under $10.00");
          dispatch({ action: ACTIONS.FAIL_SUBMIT });
          dispatch({
            action: ACTIONS.SET_ERROR_MESSAGE,
            payload: 'Delivery at this venue is only available for subtotals of $10.00 or more',
          });
          return;
        }
        if (fightData.address === '') {
          console.error('Address required');
          dispatch({ action: ACTIONS.FAIL_SUBMIT });
          dispatch({ action: ACTIONS.SET_ERROR_MESSAGE, payload: 'Please input valid address.' });
          return;
        }
        if (!isLoaded) {
          console.error('Google Maps API is not loaded.');
          dispatch({ action: ACTIONS.FAIL_SUBMIT });
          dispatch({
            action: ACTIONS.SET_ERROR_MESSAGE,
            payload: 'Internal error. Please refresh and try again.',
          });
          return;
        }
        const restaurantLocation = await getAddressLocation(geocoderRef.current, appCtx.restaurant.address);
        if (restaurantLocation === null) {
          console.warn('Could not get geolocation of restaurant address:', appCtx.restaurant.address);
        } else if (
          deliveryInfo.isLatLngReliable &&
          !areLocationsInRange(
            new window.google.maps.LatLng(deliveryInfo.lat, deliveryInfo.lng),
            restaurantLocation,
            DELIVERY_RANGE,
          )
        ) {
          console.warn('Delivery address is not in range of venue.');
          dispatch({ action: ACTIONS.FAIL_SUBMIT });
          dispatch({
            action: ACTIONS.SET_ERROR_MESSAGE,
            payload: 'Given address is not within range of venue.',
          });
          return;
        }
      }
      try {
        const fightResponse = await submitFight(authCtx.tokens.idToken, fightData);
        dispatch({ action: ACTIONS.SET_FOODFIGHT_ID, payload: fightResponse.created_bet.id });
        dispatch({ action: ACTIONS.FAIL_SUBMIT });
      } catch (e) {
        console.error(`Failed to submit fight: ${e}`);
      }
      if (fight.challenger.name !== 'New Referal Fighter') {
        pageCtx.modals.startFight.current.dismiss();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fight.items,
    fight.payment,
    fight.game,
    fight.outcome,
    fight.challenger,
    fight.changedDeliveryAddress,
    fight.deliveryLatitude,
    fight.deliveryLongitude,
    fight.isChangedAddressAutocompleted,
    fight.deliveryInstructions,
    fight.foodfightId,
    submitStripeForm,
    appCtx.deliveryAddress,
    appCtx.deliveryLatitude,
    appCtx.deliveryLongitude,
    appCtx.isAddressAutocompleted,
    appCtx.isDelivery,
    appCtx.restaurant?.id,
    appCtx.restaurant?.name,
    appCtx.restaurant?.address,
    isLoaded,
    authCtx.tokens.idToken,
    pageCtx.modals.startFight,
  ]);

  useEffect(() => {
    if (
      fight.submitted &&
      fight.pageIndex === 3 &&
      fight.game &&
      fight.outcome &&
      fight.challenger &&
      fight.items.length > 0 &&
      !fight.isReferFriendModalOpen &&
      !fight.isPaymentRequired
    ) {
      handleSubmit();
    }
    if (
      fight.submitted &&
      fight.pageIndex === 4 &&
      fight.game &&
      fight.outcome &&
      fight.challenger &&
      fight.items.length > 0 &&
      fight.isPaymentRequired
    ) {
      handleSubmit();
    }
  }, [
    fight.submitted,
    fight.pageIndex,
    fight.game,
    fight.outcome,
    fight.challenger,
    fight.items.length,
    handleSubmit,
    fight.isReferFriendModalOpen,
    fight.isPaymentRequired,
  ]);

  const memoizedValue = useMemo(
    () => ({
      ...fight,
      isPaymentComplete,
      setPaymentComplete,
      pageCount,
      navRef,
      dispatch,
      formRef,
      submitStripeForm,
      footerNav,
      matchModalRef,
      referModalRef,
    }),
    [fight, isPaymentComplete, setPaymentComplete, pageCount, navRef, dispatch, formRef, submitStripeForm],
  );

  return <StartFightContext.Provider value={memoizedValue}>{children}</StartFightContext.Provider>;
}

export { StartFightContext, StartFightContextProvider };
